/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useFetchEquitySecurities } from '@equitySecurityHooks';
import { useFetchDebtSecurities } from '@debtSecurityHooks';
import { useFetchDerivativeSecurities } from '@derivativeSecurityHooks';
import { calculateEquityProfitLoss } from '@equityUtils';

type SecuritiesOverview = {
  totalValue: number;
  equityValue: number;
  debtValue: number;
  derivativesValue: number;
  distribution: {
    equity: number;
    debt: number;
    derivatives: number;
  };
  counts: {
    equity: number;
    debt: number;
    derivatives: number;
  };
}

export function useSecuritiesOverview(searchQuery?: string) {
  const { user } = useAuth();
  const { data: equitySecurities, isLoading: equityLoading } = useFetchEquitySecurities();
  const { data: debtSecurities, isLoading: debtLoading } = useFetchDebtSecurities();
  const { data: derivativeSecurities, isLoading: derivativeLoading } = useFetchDerivativeSecurities();

  return useQuery({
    queryKey: ['securities_overview', user?.id, equitySecurities, debtSecurities, derivativeSecurities, searchQuery],
    queryFn: async (): Promise<SecuritiesOverview> => {
      const lowerQuery = searchQuery?.toLowerCase() || '';

      const filteredEquity = (equitySecurities || []).filter((s: any) => 
        !lowerQuery || 
        s.company_name?.toLowerCase().includes(lowerQuery) || 
        s.ticker_symbol?.toLowerCase().includes(lowerQuery) || 
        s.account_number?.toLowerCase().includes(lowerQuery) || 
        s.member_number?.toLowerCase().includes(lowerQuery)
      );

      const filteredDebt = (debtSecurities || []).filter((s: any) =>
        !lowerQuery || 
        s.issuer_name?.toLowerCase().includes(lowerQuery) || 
        s.bond_type?.toLowerCase().includes(lowerQuery) || 
        s.account_number?.toLowerCase().includes(lowerQuery) || 
        s.member_number?.toLowerCase().includes(lowerQuery)
      );

      const filteredDerivatives = (derivativeSecurities || []).filter((s: any) =>
        !lowerQuery || 
        s.underlying_asset?.toLowerCase().includes(lowerQuery) || 
        s.derivative_type?.toLowerCase().includes(lowerQuery) || 
        s.account_number?.toLowerCase().includes(lowerQuery) || 
        s.member_number?.toLowerCase().includes(lowerQuery)
      );

      // Calculate equity value (current market value)
      const equityValue = filteredEquity.reduce((sum, security: any) => {
        const { currentValue } = calculateEquityProfitLoss(
          security.shares_owned,
          security.purchase_price,
          security.current_price
        );
        return sum + currentValue;
      }, 0);

      // Calculate debt value (face value)
      const debtValue = filteredDebt.reduce((sum, security: any) => {
        return sum + (security.face_value || 0);
      }, 0);

      // Calculate derivatives value (current value based on contract size and prices)
      const derivativesValue = filteredDerivatives.reduce((sum, security: any) => {
        if (security.derivative_type === 'option' && security.premium_paid) {
          return sum + (security.premium_paid * security.contract_size);
        } else if (security.derivative_type === 'future' && security.contract_price) {
          return sum + (security.contract_price * security.contract_size);
        }
        return sum;
      }, 0);

      const totalValue = equityValue + debtValue + derivativesValue;

      // Calculate distribution percentages
      const distribution = {
        equity: totalValue > 0 ? (equityValue / totalValue) * 100 : 0,
        debt: totalValue > 0 ? (debtValue / totalValue) * 100 : 0,
        derivatives: totalValue > 0 ? (derivativesValue / totalValue) * 100 : 0,
      };

      // Get counts
      const counts = {
        equity: filteredEquity.length,
        debt: filteredDebt.length,
        derivatives: filteredDerivatives.length,
      };

      return {
        totalValue,
        equityValue,
        debtValue,
        derivativesValue,
        distribution,
        counts,
      };
    },
    enabled: !!user && !equityLoading && !debtLoading && !derivativeLoading,
  });
}
